import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight, ChevronDown, ChevronUp, Flag } from 'lucide-react';

interface DriverStanding {
  pos: number;
  driver: string;
  team: string;
  points: number;
  flag: string;
}

// F1 2026 standings after Canadian GP (Round 9) — Antonelli 4th straight win
const ROUND_LABEL = 'After Canadian GP (R9)';
const STANDINGS: DriverStanding[] = [
  { pos: 1, driver: 'K. Antonelli', team: 'Mercedes', points: 176, flag: '🇮🇹' },
  { pos: 2, driver: 'G. Russell', team: 'Mercedes', points: 133, flag: '🇬🇧' },
  { pos: 3, driver: 'L. Norris', team: 'McLaren', points: 110, flag: '🇬🇧' },
  { pos: 4, driver: 'L. Hamilton', team: 'Ferrari', points: 94, flag: '🇬🇧' },
  { pos: 5, driver: 'M. Verstappen', team: 'Red Bull', points: 91, flag: '🇳🇱' },
];

// Next race info
const NEXT_RACE = {
  name: 'Spanish GP',
  date: new Date(Date.UTC(2026, 5, 22, 13, 0)), // June 22, 2026 13:00 UTC
  circuit: 'Circuit de Barcelona-Catalunya',
};

function daysUntilRace(): number {
  const now = Date.now();
  const diff = NEXT_RACE.date.getTime() - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// Team colors for visual flair
const TEAM_COLOR: Record<string, string> = {
  Mercedes: 'text-teal-400',
  McLaren: 'text-orange-400',
  'Red Bull': 'text-blue-400',
  Ferrari: 'text-red-400',
};

export function F1StandingsWidget() {
  const [collapsed, setCollapsed] = useState(false);
  const days = daysUntilRace();

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-red-500/5 to-transparent text-left"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10 flex-shrink-0">
          <Trophy className="h-4 w-4 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary text-sm">F1 2026 Standings</h3>
          <span className="text-[10px] text-text-tertiary">{ROUND_LABEL}</span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronUp className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        )}
      </button>

      {!collapsed && (
        <>
          {/* Next race banner */}
          {days > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border-primary bg-bg-tertiary/50">
              <Flag className="h-3.5 w-3.5 text-text-tertiary flex-shrink-0" />
              <span className="text-xs text-text-secondary">
                <span className="font-medium">{NEXT_RACE.name}</span>
                {' '}in{' '}
                <span className="font-bold text-red-400 tabular-nums">{days}d</span>
              </span>
            </div>
          )}

          {/* Standings list */}
          <div className="divide-y divide-border-primary">
            {STANDINGS.map((d) => (
              <div key={d.pos} className="flex items-center gap-3 px-4 py-2.5">
                <span className={`w-5 text-center text-xs font-bold tabular-nums ${d.pos === 1 ? 'text-yellow-400' : 'text-text-tertiary'}`}>
                  {d.pos}
                </span>
                <span className="text-sm flex-shrink-0">{d.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{d.driver}</p>
                  <p className={`text-[11px] ${TEAM_COLOR[d.team] ?? 'text-text-tertiary'}`}>{d.team}</p>
                </div>
                <span className="text-sm font-bold text-text-primary tabular-nums">{d.points}</span>
                <span className="text-[10px] text-text-tertiary">pts</span>
              </div>
            ))}
          </div>

          <Link
            to="/guides/f1-2026-season-guide"
            className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            Full season guide
            <ChevronRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </div>
  );
}
